/**
 * @file ShipmentCardSummary.tsx
 * @description Левая колонка карточки отгрузки: бейджи и сводка
 * (phase19.12, вынесено в phase19.13 шаг 6).
 *
 * Отделено от `OrderShipmentCard`: с историей статусов, бейджем «зависла» и
 * строкой про неудачное вручение карточка перестала помещаться в лимит
 * 200 строк.
 */

import { ExternalLink, MapPin, TriangleAlert } from "lucide-react";

import { ShipmentProblemNote, ShipmentStaleBadge } from "./ShipmentProblemNotes";
import { Badge } from "../ui/badge";
import type { DeliveryStage } from "../../lib/api-delivery-monitoring";
import {
  SHIPMENT_CARRIER_LABEL,
  SHIPMENT_STATUS_LABEL,
  type ShipmentDto,
} from "../../lib/api-orders";
import { sdekTrackingUrl } from "../../lib/api-carriers-sdek";
import { formatRub, formatWeight } from "../../lib/delivery-quote";
import { ORDER_REFUND_TEXT } from "../../lib/order-summary-lines";
import {
  shipmentTrackLabel,
  type ShipmentStalenessBadge,
} from "../../lib/shipment-state";

interface Props {
  shipment: ShipmentDto;
  /** Отгрузка уже у перевозчика. */
  handed: boolean;
  /** По заказу возвращали деньги — предупреждаем на черновиках. */
  orderRefunded: boolean;
  /** Цена доставки для клиента, если тариф отгрузки совпал с заказом. */
  clientPrice?: number | null;
  /** Пометка «зависла», посчитанная на клиенте. */
  stale: ShipmentStalenessBadge | null;
  /** Этап последнего события ленты и его комментарий. */
  lastStage: DeliveryStage | null;
  lastComment: string | null;
}

export function ShipmentCardSummary({
  shipment: s,
  handed,
  orderRefunded,
  clientPrice,
  stale,
  lastStage,
  lastComment,
}: Props) {
  const isSdek = s.carrier === "SDEK";
  const editable = !handed;
  const packages = s.packages ?? [];
  const weight = packages.reduce((sum, p) => sum + p.weightG, 0);

  const deliveryLine = [
    s.tariffCode !== null ? `Тариф ${s.tariffCode}` : null,
    s.pickupPointCode ? `ПВЗ ${s.pickupPointCode}` : null,
    packages.length > 0 ? `мест ${packages.length}` : null,
    weight > 0 ? formatWeight(weight) : null,
    clientPrice !== null && clientPrice !== undefined
      ? `клиенту ${formatRub(clientPrice)}`
      : null,
    s.carrierPrice !== null ? `СДЭК ${formatRub(s.carrierPrice)}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline">{SHIPMENT_STATUS_LABEL[s.status]}</Badge>
        <Badge variant="secondary">{SHIPMENT_CARRIER_LABEL[s.carrier]}</Badge>
        {isSdek && handed ? <Badge variant="primary">Передана в СДЭК</Badge> : null}
        {stale ? <ShipmentStaleBadge badge={stale} /> : null}
        {s.trackNumber ? <span className="font-mono text-xs">{s.trackNumber}</span> : null}
        {isSdek && s.trackNumber ? (
          <a
            href={sdekTrackingUrl(s.trackNumber)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            <ExternalLink className="size-3" />
            Отследить
          </a>
        ) : null}
      </div>

      <p className="mt-1 text-sm text-on-surface-variant">
        {s.itemsCount} поз. · {new Date(s.createdAt).toLocaleDateString("ru-RU")}
        {s.shippedAt
          ? ` · отгружено ${new Date(s.shippedAt).toLocaleDateString("ru-RU")}`
          : ""}
        {s.deliveredAt
          ? ` · доставлено ${new Date(s.deliveredAt).toLocaleDateString("ru-RU")}`
          : ""}
      </p>

      {deliveryLine ? (
        <p className="mt-1 flex items-start gap-1.5 text-sm">
          <MapPin className="mt-0.5 size-4 shrink-0 text-on-surface-variant" />
          <span className="min-w-0">{deliveryLine}</span>
        </p>
      ) : null}

      {orderRefunded && editable ? (
        // Янтарная строка на всю ширину колонки: на 360px она стоит прямо
        // над блоком кнопок, на десктопе — слева от «Передать в СДЭК».
        <p className="mt-2 flex w-full items-start gap-1.5 rounded-md bg-warning/12 px-2 py-1.5 text-sm">
          <TriangleAlert className="mt-0.5 size-4 shrink-0 text-warning" />
          <span className="min-w-0 break-words">{ORDER_REFUND_TEXT.shipment}</span>
        </p>
      ) : null}

      {isSdek && s.tariffCode === null && editable ? (
        <p className="mt-1 text-xs text-warning">
          Тариф не рассчитан — заявка уйдёт с параметрами по умолчанию.
        </p>
      ) : null}

      {isSdek && handed ? (
        <p className="mt-1 text-sm">Заявка создана · {shipmentTrackLabel(s)}</p>
      ) : null}

      {s.notes ? <p className="mt-1 text-xs text-on-surface-variant">{s.notes}</p> : null}

      <ShipmentProblemNote stage={lastStage} comment={lastComment} />
    </div>
  );
}
